import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import './login-styles.css';
import QuestionCard from './components/QuestionCard';
import ResultsSection from './components/ResultsSection';
import ProgressIndicator from './components/ProgressIndicator';
import EvaluationModal from './components/EvaluationModal';
import Login from './Login';
import useAuth from './useAuth';
import './utils/iframeHeightManager';

// Questions without section headers or competency names to avoid bias
const QUESTIONS = [
  { id: 1, text: "Tisztában vagyok a vezetői erősségeimmel és azokkal a területekkel, ahol még fejlődnöm kell.", competency: 'onismeret' },
  { id: 2, text: "Tudom, hogy a hangulatom és a viselkedésem milyen hatással van a csapatomra.", competency: 'onismeret' },
  { id: 3, text: "Stresszes, nagy nyomással járó helyzetekben is képes vagyok higgadt és megfontolt maradni.", competency: 'onuralom' },
  { id: 4, text: "Mielőtt reagálnék egy nehéz helyzetre, időt adok magamnak a gondolkodásra.", competency: 'onuralom' },
  { id: 5, text: "A tetteim mindig összhangban vannak azzal, amit mondok és az általam képviselt értékekkel.", competency: 'integritas' },
  { id: 6, text: "Ha hibázom, azt nyíltan felvállalom a csapatom előtt.", competency: 'integritas' },
  { id: 7, text: "Aktívan keresem a visszajelzéseket a munkámról, és beépítem azokat a fejlődésembe.", competency: 'tanulasi_agilitas' },
  { id: 8, text: "A kudarcokra tanulási lehetőségként tekintek, nem pedig személyes kudarcként.", competency: 'tanulasi_agilitas' },
  { id: 9, text: "Egy komolyabb szakmai nehézség vagy visszautasítás után is képes vagyok talpra állni és továbbmenni.", competency: 'reziliencia' },
  { id: 10, text: "Képes vagyok megőrizni az optimizmusomat és a motivációmat a kihívásokkal teli időszakokban is.", competency: 'reziliencia' },
  { id: 11, text: "Biztosítom, hogy a csapatom minden tagja tisztában legyen a célokkal és az elvárásokkal.", competency: 'kommunikacio' },
  { id: 12, text: "Aktívan és nyitottan hallgatom meg a munkatársaim véleményét, még akkor is, ha az eltér az enyémtől.", competency: 'kommunikacio' },
  { id: 13, text: "Igyekszem megérteni a csapattagjaim nézőpontját és a döntéseik mögött rejlő motivációkat.", competency: 'empatia' },
  { id: 14, text: "Észreveszem, ha egy munkatársamnak nehézségei vannak, és proaktívan felajánlom a segítségemet.", competency: 'empatia' },
  { id: 15, text: "Rendszeresen elismerem a jó teljesítményt és a csapattagok hozzájárulását.", competency: 'motivacio' },
  { id: 16, text: "Képes vagyok lelkesíteni a csapatomat és fenntartani a lendületet a közös célok elérése érdekében.", competency: 'motivacio' },
  { id: 17, text: "Hatékonyan osztom ki a feladatokat a csapattagok erősségeinek és fejlődési igényeinek megfelelően.", competency: 'delegals' },
  { id: 18, text: "Amikor delegálok egy feladatot, megadom a szükséges szabadságot és bizalmat a végrehajtáshoz.", competency: 'delegals' },
  { id: 19, text: "A csapaton belüli konfliktusokat nyíltan, de konstruktívan kezelem.", competency: 'konfliktuskezeles' },
  { id: 20, text: "Arra törekszem, hogy a konfliktusokból mindenki számára elfogadható, \"win-win\" megoldás szülessen.", competency: 'konfliktuskezeles' },
  { id: 21, text: "Időt szánok arra, hogy segítsem a munkatársaimat a szakmai fejlődésükben.", competency: 'coaching' },
  { id: 22, text: "Kérdésekkel és iránymutatással segítem a csapatom tagjait, hogy maguk jöjjenek rá a megoldásokra.", competency: 'coaching' },
  { id: 23, text: "Képes vagyok a napi operatív feladatok mellett a hosszú távú célokra és a \"nagy képre\" is fókuszálni.", competency: 'strategiai_gondolkodas' },
  { id: 24, text: "A csapatom számára is érthetővé teszem, hogyan járul hozzá a munkájuk a cég stratégiai céljaihoz.", competency: 'strategiai_gondolkodas' },
  { id: 25, text: "Szervezeti változások során világosan kommunikálom a változás okait és előnyeit, hogy csökkenetsem a bizonytalanságot.", competency: 'valtozasmenedzsment' },
  { id: 26, text: "Proaktívan kezelem a változásokkal szembeni ellenállást, és támogatom a csapatomat az alkalmazkodásban.", competency: 'valtozasmenedzsment' },
  { id: 27, text: "Döntéseimet a rendelkezésre álló adatok és tények alapos mérlegelése után hozom meg.", competency: 'dontes' },
  { id: 28, text: "Képes vagyok határozott döntéseket hozni akkor is, ha nem áll rendelkezésemre minden információ.", competency: 'dontes' },
  { id: 29, text: "Bátorítom a csapatomat, hogy kísérletezzenek új ötletekkel és megoldásokkal.", competency: 'innovacio' },
  { id: 30, text: "Támogató légkört teremtek, ahol a munkatársak mernek hibázni és tanulni belőle.", competency: 'innovacio' },
  { id: 31, text: "Aktívan keresem az együttműködési lehetőségeket más csapatokkal, részlegekkel.", competency: 'egyuttmukodes' },
  { id: 32, text: "A saját csapatom érdekei mellett a szervezet egészének érdekeit is szem előtt tartom.", competency: 'egyuttmukodes' }
];

// Competency definitions (hidden from user during test)
const COMPETENCIES = {
  'onismeret': { name: 'Önismeret', section: 'Önmagunk vezetése' },
  'onuralom': { name: 'Önuralom', section: 'Önmagunk vezetése' },
  'integritas': { name: 'Integritás', section: 'Önmagunk vezetése' },
  'tanulasi_agilitas': { name: 'Tanulási agilitás', section: 'Önmagunk vezetése' },
  'reziliencia': { name: 'Reziliencia', section: 'Önmagunk vezetése' },
  'kommunikacio': { name: 'Kommunikáció', section: 'Mások vezetése' },
  'empatia': { name: 'Empátia', section: 'Mások vezetése' },
  'motivacio': { name: 'Motiváció', section: 'Mások vezetése' },
  'delegals': { name: 'Delegálás', section: 'Mások vezetése' },
  'konfliktuskezeles': { name: 'Konfliktuskezelés', section: 'Mások vezetése' },
  'coaching': { name: 'Coaching', section: 'Mások vezetése' },
  'strategiai_gondolkodas': { name: 'Stratégiai gondolkodás', section: 'Szervezet vezetése' },
  'valtozasmenedzsment': { name: 'Változásmenedzsment', section: 'Szervezet vezetése' },
  'dontes': { name: 'Döntéshozatal', section: 'Szervezet vezetése' },
  'innovacio': { name: 'Innováció', section: 'Szervezet vezetése' },
  'egyuttmukodes': { name: 'Együttműködés', section: 'Szervezet vezetése' }
};

// Fisher-Yates shuffle algorithm for randomizing questions
const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

function App() {
  const { isAuthenticated, login, logout } = useAuth();
  const [questions] = useState(() => shuffleArray(QUESTIONS));
  const [answers, setAnswers] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [showResults, setShowResults] = useState(false);
  const [showEvalModal, setShowEvalModal] = useState(false);

  const totalQuestions = questions.length;
  const answeredCount = Object.keys(answers).length;
  const progress = (answeredCount / totalQuestions) * 100;
  const allAnswered = answeredCount === totalQuestions && totalQuestions > 0;

  // Handle answer selection
  const handleAnswer = useCallback((questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));

    // Auto-advance to next unanswered question after a short delay
    setTimeout(() => {
      const nextUnansweredIndex = questions.findIndex((q, index) =>
        index > currentQuestionIndex && !answers[q.id] && !answers[q.id] === value
      );

      if (nextUnansweredIndex !== -1) {
        setCurrentQuestionIndex(nextUnansweredIndex);
      } else if (currentQuestionIndex < questions.length - 1) {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
      }
    }, 300);
  }, [answers, currentQuestionIndex, questions]);

  // Navigate between questions
  const goToQuestion = useCallback((index) => {
    setCurrentQuestionIndex(Math.max(0, Math.min(index, questions.length - 1)));
  }, [questions.length]);

  const goToPrevious = useCallback(() => {
    goToQuestion(currentQuestionIndex - 1);
  }, [currentQuestionIndex, goToQuestion]);

  const goToNext = useCallback(() => {
    goToQuestion(currentQuestionIndex + 1);
  }, [currentQuestionIndex, goToQuestion]);

  // Open evaluation modal
  const openEvalModal = useCallback(() => {
    setShowEvalModal(true);
  }, []);

  const closeEvalModal = useCallback(() => {
    setShowEvalModal(false);
  }, []);

  // After successful submission, show results
  const handleSubmissionSuccess = useCallback(() => {
    setShowEvalModal(false);
    setShowResults(true);
  }, []);

  // Restart test
  const restartTest = useCallback(() => {
    setAnswers({});
    setCurrentQuestionIndex(0);
    setShowResults(false);
    setShowEvalModal(false);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (showResults || showEvalModal) return;

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          goToPrevious();
          break;
        case 'ArrowRight':
          e.preventDefault();
          goToNext();
          break;
        case '1':
        case '2':
        case '3':
        case '4':
        case '5':
          e.preventDefault();
          handleAnswer(questions[currentQuestionIndex].id, parseInt(e.key));
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentQuestionIndex, goToNext, goToPrevious, handleAnswer, questions, showResults, showEvalModal]);

  // Show login if not authenticated
  if (!isAuthenticated) {
    return <Login onLogin={login} />;
  }

  if (showResults) {
    return (
      <ResultsSection
        answers={answers}
        questions={questions}
        competencies={COMPETENCIES}
        onRestart={restartTest}
      />
    );
  }

  const currentQuestion = questions[currentQuestionIndex];

  return (
    <div className="app">
      <div className="container">
        {/* Logout button */}
        <button onClick={logout} className="logout-btn" style={{
          position: 'absolute',
          top: '20px',
          right: '20px',
          padding: '8px 16px',
          backgroundColor: 'white',
          color: '#156B5C',
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer',
          fontWeight: '600',
          fontSize: '14px',
          transition: 'all 0.3s ease'
        }}>
          Kijelentkezés
        </button>

        {/* Simple header without bias */}
        <header className="header">
          <h1 className="title">Kompetencia Felmérés</h1>
          <p className="subtitle">
            Értékeld őszintén, mennyire jellemzőek rád az alábbi állítások 1-től 5-ig.
          </p>
        </header>

        {/* Rating scale explanation */}
        <div className="rating-scale">
          <h3>Értékelési skála:</h3>
          <div className="scale-items">
            <div className="scale-item">
              <span className="number">1</span>
              <span className="description">Egyáltalán nem jellemző / Soha</span>
            </div>
            <div className="scale-item">
              <span className="number">2</span>
              <span className="description">Ritkán jellemző</span>
            </div>
            <div className="scale-item">
              <span className="number">3</span>
              <span className="description">Időnként jellemző</span>
            </div>
            <div className="scale-item">
              <span className="number">4</span>
              <span className="description">Gyakran jellemző</span>
            </div>
            <div className="scale-item">
              <span className="number">5</span>
              <span className="description">Teljes mértékben jellemző / Mindig</span>
            </div>
          </div>
        </div>

        {/* Question navigation */}
        <div className="question-navigation">
          <button
            onClick={goToPrevious}
            disabled={currentQuestionIndex === 0}
            className="nav-btn nav-btn-prev"
          >
            ← Előző
          </button>

          <span className="question-counter">
            {currentQuestionIndex + 1} / {totalQuestions}
          </span>

          <button
            onClick={goToNext}
            disabled={currentQuestionIndex === questions.length - 1}
            className="nav-btn nav-btn-next"
          >
            Következő →
          </button>
        </div>

        {/* Current question */}
        <QuestionCard
          question={currentQuestion}
          value={answers[currentQuestion.id]}
          onAnswer={handleAnswer}
        />

        {/* Question dots for quick navigation */}
        <div className="question-dots">
          {questions.map((_, index) => (
            <button
              key={index}
              onClick={() => goToQuestion(index)}
              className={`question-dot ${
                index === currentQuestionIndex ? 'active' : ''
              } ${
                answers[questions[index].id] ? 'answered' : ''
              }`}
            />
          ))}
        </div>

        {/* Evaluation button - appears when all questions are answered */}
        {allAnswered && (
          <div className="submit-section">
            <button
              onClick={openEvalModal}
              className="submit-btn"
              style={{
                backgroundColor: '#156B5C',
                color: 'white',
                padding: '14px 28px',
                fontSize: '18px',
                fontWeight: '600',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              Kérem a teszt kiértékelését
            </button>
          </div>
        )}

        {/* Progress indicator */}
        <ProgressIndicator
          progress={progress}
          answeredCount={answeredCount}
          totalQuestions={totalQuestions}
        />
      </div>

      {/* Evaluation Modal */}
      <EvaluationModal
        isOpen={showEvalModal}
        onClose={closeEvalModal}
        onSubmitSuccess={handleSubmissionSuccess}
        answers={answers}
        questions={questions}
        competencies={COMPETENCIES}
      />
    </div>
  );
}

export default App;
